import { createRootRoute, Link, Outlet, useRouterState } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { AppShell, Button, PageShell, Spinner, StatusBar } from '@viquard/doc-ui';
import { Suspense, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArticleSearchDialog,
  Brand,
  GroupSwitcher,
  MobileNavTrigger,
  NotificationsTrigger,
  SearchTrigger,
  TopbarNav,
  UserMenu,
} from '@/components/topbar';
import {
  clearAuthSessionHint,
  clearAuthSensitiveBrowserState,
  clearViewerScopedQueryCache,
  hasAuthSessionHint,
  useMe,
} from '@/features/auth/api';
import { useSpaAnalytics } from '@/features/analytics/use-spa-analytics';
import { useNotificationsRealtime } from '@/features/notifications/api';
import { ActiveGroupProvider } from '@/features/groups/active-group-context';
import { useScrolled } from '@/hooks/use-scrolled';
import { ApiRequestError } from '@/lib/api';
import { applyPageHead, resolvePageHead } from '@/lib/page-head';
import { hasIcpMetadata, siteMetadata } from '@/lib/site-metadata';

const ACTIVE_GROUP_STORAGE_KEY = 'personal-blog.activeGroupSlug';

function readStoredActiveGroup(): string | null | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const raw = window.localStorage.getItem(ACTIVE_GROUP_STORAGE_KEY);
    if (raw === null) return undefined;
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'string' || parsed === null ? parsed : undefined;
  } catch {
    return undefined;
  }
}

function RouteFallback(): ReactNode {
  return (
    <div role="status" aria-live="polite" className="flex flex-1 items-center justify-center py-12" style={{ color: 'var(--vq-ink-3)' }}>
      <Spinner size="md" />
    </div>
  );
}

interface AuthState {
  authenticated: boolean;
  loading: boolean;
  displayName: string;
  email: string | null;
  userId: string | null;
  sessionKey: string | undefined;
  sessionRejected: boolean;
}

function useAuthState(enabled: boolean): AuthState {
  const me = useMe({ enabled });
  const sessionKey = resolveSessionKey(me);
  if (!enabled && me.data === undefined) {
    return {
      authenticated: false,
      loading: false,
      displayName: '',
      email: null,
      userId: null,
      sessionKey,
      sessionRejected: false,
    };
  }
  if (me.isPending) {
    return {
      authenticated: false,
      loading: true,
      displayName: '',
      email: null,
      userId: null,
      sessionKey,
      sessionRejected: false,
    };
  }
  if (me.error instanceof ApiRequestError && me.error.status === 401) {
    return {
      authenticated: false,
      loading: false,
      displayName: '',
      email: null,
      userId: null,
      sessionKey,
      sessionRejected: true,
    };
  }
  if (me.data == null) {
    return {
      authenticated: false,
      loading: false,
      displayName: '',
      email: null,
      userId: null,
      sessionKey,
      sessionRejected: false,
    };
  }
  return {
    authenticated: true,
    loading: false,
    displayName: me.data.user.displayName,
    email: me.data.user.email,
    userId: me.data.user.id,
    sessionKey,
    sessionRejected: false,
  };
}

function resolveSessionKey(me: ReturnType<typeof useMe>): string | undefined {
  if (me.error instanceof ApiRequestError && me.error.status === 401) return 'guest';
  if (me.data?.user.id !== undefined) return `user:${me.data.user.id}`;
  if (me.isPending || me.error !== null) return undefined;
  return 'guest';
}

function GuestActions(): ReactNode {
  const { t } = useTranslation('common');
  const { location } = useRouterState();
  // Keep auth entry pages quiet; the forms already cross-link login and
  // registration in their footers.
  if (location.pathname === '/login' || location.pathname === '/register' || location.pathname === '/reset-password') {
    return null;
  }
  const next =
    typeof window === 'undefined' ? location.pathname : `${window.location.pathname}${window.location.search}${window.location.hash}`;
  return (
    <div className="topbar-guest-actions">
      <Link to="/login" search={{ next }} style={{ textDecoration: 'none' }}>
        <Button type="button" variant="secondary" size="sm">
          {t('auth.signIn')}
        </Button>
      </Link>
      <Link to="/register" style={{ textDecoration: 'none' }}>
        <Button type="button" variant="primary" size="sm">
          {t('auth.createAccount')}
        </Button>
      </Link>
    </div>
  );
}

function SiteStatusBar(): ReactNode {
  const { t } = useTranslation('common');
  if (!hasIcpMetadata()) return null;
  return (
    <StatusBar
      aria-label={t('siteStatus.label')}
      className="site-statusbar"
      left={
        <a className="site-statusbar__link" href={siteMetadata.icpUrl} target="_blank" rel="noreferrer">
          {siteMetadata.icpNumber}
        </a>
      }
    />
  );
}

function RootLayout(): ReactNode {
  const routeLocation = useRouterState({ select: (state) => state.location });
  const { i18n } = useTranslation('common');
  const analyticsPath = typeof window === 'undefined' ? routeLocation.pathname : `${window.location.pathname}${window.location.search}`;
  const pageLanguage = i18n.resolvedLanguage ?? i18n.language;
  const pageHead = useMemo(
    () => resolvePageHead(routeLocation.pathname, siteMetadata.name, pageLanguage),
    [pageLanguage, routeLocation.pathname],
  );
  const auth = useAuthState(hasAuthSessionHint());
  const qc = useQueryClient();
  const lastSessionKey = useRef<string | null>(null);
  // Topbar is transparent at the top of the page; pills appear once the
  // reader starts scrolling. The threshold is set just past zero so the
  // chrome locks in immediately on any meaningful scroll, not on every
  // 1-px wobble from a touch device's elastic bounce.
  const scrolled = useScrolled(8);
  const [activeSlug, setActiveSlug] = useState<string | null | undefined>(readStoredActiveGroup);
  const [searchOpen, setSearchOpen] = useState(false);
  useSpaAnalytics(analyticsPath);
  useNotificationsRealtime(auth.authenticated);

  useEffect(() => {
    applyPageHead(pageHead);
  }, [pageHead]);

  useEffect(() => {
    if (auth.sessionKey === undefined) return;
    if (lastSessionKey.current === null) {
      lastSessionKey.current = auth.sessionKey;
      return;
    }
    if (lastSessionKey.current === auth.sessionKey) return;
    lastSessionKey.current = auth.sessionKey;
    clearAuthSensitiveBrowserState();
    setActiveSlug(null);
    void clearViewerScopedQueryCache(qc);
  }, [auth.sessionKey, qc]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'k' && event.key !== 'K') return;
      if (!event.metaKey && !event.ctrlKey) return;
      event.preventDefault();
      setSearchOpen(true);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  useEffect(() => {
    if (auth.loading || auth.authenticated) return;
    if (auth.sessionRejected) clearAuthSessionHint();
    clearAuthSensitiveBrowserState();
    setActiveSlug(null);
  }, [auth.authenticated, auth.loading, auth.sessionRejected]);

  useEffect(() => {
    if (typeof window === 'undefined' || activeSlug === undefined) return;
    if (activeSlug === null) {
      window.localStorage.removeItem(ACTIVE_GROUP_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(ACTIVE_GROUP_STORAGE_KEY, JSON.stringify(activeSlug));
  }, [activeSlug]);

  // Each topbar slot is wrapped in a `.topbar-island` div. The pill
  // chrome (paper-raised background, hairline border, soft shadow,
  // backdrop blur) lives on the wrapper — NOT on the AppShell grid
  // cell — so the visible pill is exactly content-width regardless of
  // how the grid sizes the parent track. The doc-ui shell still owns
  // sticky positioning + 3-track layout. Empty wrappers (e.g. center
  // when guest viewer hides the nav) collapse via `:empty` so no ghost
  // pill paints. See globals.css §"Floating topbar islands".
  const topLeft = (
    <div className="topbar-island topbar-island--left">
      {/* Hamburger affordance for narrow viewports — doc-ui hides the
          centre `<TopbarNav />` ≤1023 px, so phones / tablets need an
          alternative way to reach Articles / Calendar / Groups.  CSS
          shows the button only ≤1023 px (`.topbar-mobile-trigger`). */}
      <MobileNavTrigger authenticated={auth.authenticated} />
      <Brand />
      {auth.authenticated ? (
        <GroupSwitcher
          activeSlug={activeSlug ?? undefined}
          onActiveSlugChange={(next) => {
            setActiveSlug(next);
          }}
        />
      ) : null}
    </div>
  );

  const topCenter = (
    <div className="topbar-island topbar-island--center">
      <TopbarNav authenticated={auth.authenticated} />
    </div>
  );

  // Right island keeps search public while auth-gated affordances and the
  // avatar menu depend on session state. Theme + language switching live
  // exclusively in /settings (reachable from the user-menu's Settings entry)
  // to keep the header focused on navigation and presence.
  const topRight = (
    <div className="topbar-island topbar-island--right">
      <SearchTrigger
        onOpen={() => {
          setSearchOpen(true);
        }}
      />
      {auth.authenticated ? <NotificationsTrigger /> : null}
      {auth.authenticated ? <UserMenu displayName={auth.displayName} email={auth.email} userId={auth.userId} /> : <GuestActions />}
    </div>
  );
  const showSiteStatusBar = hasIcpMetadata() && !auth.loading && !auth.authenticated && routeLocation.pathname === '/';

  return (
    <ActiveGroupProvider activeSlug={activeSlug} setActiveSlug={setActiveSlug}>
      <>
        <AppShell
          preset="reading"
          shellStyle="floating"
          sidebarPosition="hidden"
          rightPanelPosition="hidden"
          statusBarVisibility={showSiteStatusBar ? 'visible' : 'hidden'}
          pageWidth="full"
          pageMargin="tight"
          topBar="standard"
          className={scrolled ? 'app-shell--floating-topbar app-shell--scrolled' : 'app-shell--floating-topbar'}
          mainLabel={`${siteMetadata.name} content`}
          topLeft={topLeft}
          topCenter={topCenter}
          topRight={topRight}
          statusBar={showSiteStatusBar ? <SiteStatusBar /> : undefined}
          main={
            <Suspense fallback={<RouteFallback />}>
              <Outlet />
            </Suspense>
          }
        />
        <ArticleSearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
      </>
    </ActiveGroupProvider>
  );
}

// 404 fallback. Rendered INSIDE the root component (which still mounts the
// AppShell), so the user keeps the topbar / nav and can recover. Default
// TanStack Router fallback is `<p>Not Found</p>` and triggers the dev
// warning we used to see on every typo'd link or expired routed token.
function NotFoundView(): ReactNode {
  const { t } = useTranslation('common');
  return (
    <PageShell className="not-found-shell">
      <h1 className="font-serif text-4xl font-medium" style={{ color: 'var(--vq-ink)' }}>
        {t('notFound.title')}
      </h1>
      <p className="max-w-md text-sm leading-relaxed" style={{ color: 'var(--vq-ink-3)' }}>
        {t('notFound.body')}
      </p>
      <Link to="/" style={{ textDecoration: 'none' }}>
        <Button type="button" variant="primary" size="md" shape="pill">
          {t('notFound.backHome')}
        </Button>
      </Link>
    </PageShell>
  );
}

export const Route = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFoundView,
});
